import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronDown, Clock3, Heart, ListChecks, Package, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { cn } from "@/lib/utils";
import { colorDotClass, colorDotStyle } from "@/pages/daycare-hotel/accommodations/colors";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { getPetStayNotesLabelKey } from "@/pages/daycare-hotel/reservation/petStayNotesLabel";
import {
  petAlertColorPresentation,
  type PetRelationshipKind,
} from "@/pages/daycare-hotel/reservation/petProfileModel";
import {
  useDaycareHotelPetsByIds,
  usePetDaycareHotelInitialDraft,
} from "./usePetDaycareHotelDraft";
import { useStayPacks } from "@/hooks/useStayPacks";

const RELATIONSHIP_TONE: Record<PetRelationshipKind, string> = {
  likes: "border-state-success/40 bg-state-success/10",
  dislikes: "border-state-danger/40 bg-state-danger/10",
  neutral: "border-border bg-muted/40",
};

const RELATIONSHIP_DOT: Record<PetRelationshipKind, string> = {
  likes: "bg-state-success",
  dislikes: "bg-state-danger",
  neutral: "bg-muted-foreground",
};

function Block({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof AlertTriangle;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        {title}
      </p>
      {children}
    </div>
  );
}

export type PetDaycareHotelSummaryProps = {
  petId: string;
  className?: string;
  defaultOpen?: boolean;
};

/** Resumo somente leitura de creche e hotel exibido no perfil do pet. */
export function PetDaycareHotelSummary({ petId, className, defaultOpen = true }: PetDaycareHotelSummaryProps) {
  const { t } = useTranslation("daycareHotel");
  const [open, setOpen] = useState(defaultOpen);
  const { mode } = useDaycareHotelAccess();
  const { data: draft } = usePetDaycareHotelInitialDraft(mode === "none" ? null : petId);
  const { data: packs = [] } = useStayPacks();

  const alerts = (draft?.alerts ?? []).filter((alert) => alert.value.trim().length > 0);
  const storedItems = (draft?.storedItems ?? []).filter((item) => item.itemName.trim().length > 0);
  const relationships = (draft?.relationships ?? []).filter((row) => row.relatedPetId);
  const recurringActivities = (draft?.recurringActivities ?? []).filter(
    (activity) => activity.recurring && activity.description.trim().length > 0,
  );
  const selectedPacks = packs.filter((pack) => (draft?.packIds ?? []).includes(pack.id));
  const { data: relatedPetsById } = useDaycareHotelPetsByIds(
    relationships.map((relationship) => relationship.relatedPetId),
  );

  const notes = draft?.notes?.trim() ?? "";
  const hasContent =
    notes.length > 0 || selectedPacks.length > 0 || alerts.length > 0 || storedItems.length > 0 || relationships.length > 0 || recurringActivities.length > 0;

  if (mode === "none" || !draft || !hasContent) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn("rounded-lg border p-4", className)}>
      <CollapsibleTrigger asChild>
        <button type="button" className="flex w-full items-center gap-2 text-left">
          <p className="text-base font-semibold text-foreground">{t("petForm.title")}</p>
          <ChevronDown
            aria-hidden="true"
            className={cn("ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-4 space-y-5">
        {notes ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {t(getPetStayNotesLabelKey(mode), { defaultValue: t("petForm.notesLabel") })}
            </p>
            <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">{notes}</p>
          </div>
        ) : null}

        {selectedPacks.length > 0 ? (
          <Block icon={Users} title={t("petForm.packsLabel")}>
            <ul className="flex flex-wrap gap-2">
              {selectedPacks.map((pack) => (
                <li key={pack.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-sm text-foreground">
                  <span aria-hidden="true" className={cn("h-2 w-2 rounded-full", colorDotClass(pack.color))} style={colorDotStyle(pack.color)} />
                  {pack.name}
                </li>
              ))}
            </ul>
          </Block>
        ) : null}

        {alerts.length > 0 ? (
          <Block icon={AlertTriangle} title={t("petForm.alertsLabel")}>
            <ul className="flex flex-wrap gap-2">
              {alerts.map((alert) => {
                const presentation = petAlertColorPresentation(alert.color);
                return (
                  <li
                    key={alert.id}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1 text-sm text-foreground"
                  >
                    <span
                      aria-hidden="true"
                      className={cn("h-2 w-2 shrink-0 rounded-full", presentation.dotClass)}
                      style={
                        presentation.customColor ? { backgroundColor: presentation.customColor } : undefined
                      }
                    />
                    {alert.value}
                  </li>
                );
              })}
            </ul>
          </Block>
        ) : null}

        {storedItems.length > 0 ? (
          <Block icon={Package} title={t("petForm.storedItemsLabel")}>
            <ul className="space-y-1.5">
              {storedItems.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
                >
                  <span className="font-medium text-foreground">{item.itemName}</span>
                  {item.storageLocation?.trim() ? (
                    <span className="text-xs text-muted-foreground">{item.storageLocation}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Block>
        ) : null}

        {relationships.length > 0 ? (
          <Block icon={Heart} title={t("petForm.relationshipsLabel")}>
            <ul className="grid gap-2 sm:grid-cols-2">
              {relationships.map((relationship) => {
                const relatedPet = relatedPetsById?.[relationship.relatedPetId];
                return (
                  <li
                    key={relationship.id}
                    className={cn(
                      "flex min-w-0 items-center gap-3 rounded-md border px-3 py-2",
                      RELATIONSHIP_TONE[relationship.relationship],
                    )}
                  >
                    <Avatar className="h-9 w-9 shrink-0">
                      {relatedPet?.petPhotoPath ? (
                        <AvatarImage src={relatedPet.petPhotoPath} alt={relatedPet.petName} />
                      ) : null}
                      <AvatarFallback className="bg-muted/50 text-muted-foreground">
                        <CustomIcons name="paws" className="h-4 w-4 text-current" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {relatedPet?.petName ?? "—"}
                      </p>
                      {relatedPet?.ownerName ? (
                        <p className="truncate text-xs text-muted-foreground">
                          {t("petForm.ownerPrefix")}: {relatedPet.ownerName}
                        </p>
                      ) : null}
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <span
                        aria-hidden="true"
                        className={cn("h-2 w-2 rounded-full", RELATIONSHIP_DOT[relationship.relationship])}
                      />
                      {t(`petForm.relationshipKind.${relationship.relationship}`)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Block>
        ) : null}

        {recurringActivities.length > 0 ? (
          <Block icon={ListChecks} title={t("petForm.recurringActivitiesLabel")}>
            <ul className="space-y-1.5">
              {recurringActivities.map((activity) => (
                <li key={activity.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                  <span className="font-medium text-foreground">{activity.typeName || t("activities.targetGeneral")}</span>
                  <span className="text-muted-foreground">{activity.description}</span>
                  {activity.scheduledTime ? (
                    <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock3 aria-hidden="true" className="h-3.5 w-3.5" />
                      {activity.scheduledTime}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Block>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}
